import { useState, useRef, useEffect } from 'react';
import { Send, X, Sparkles, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/propertyai/components/ui/button';
import { ScrollArea } from '@/propertyai/components/ui/scroll-area';
import { sendChatMessage, getSuggestedPrompts } from '@/propertyai/lib/api';

const MARKDOWN_IMAGE_REGEX = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+(?:\?[^\s)]*)?)\)/g;

const splitContentWithImages = (content) => {
  if (!content) return [];

  const segments = [];
  let lastIndex = 0;
  let match;

  MARKDOWN_IMAGE_REGEX.lastIndex = 0;

  while ((match = MARKDOWN_IMAGE_REGEX.exec(content)) !== null) {
    const [raw, alt, src] = match;
    const start = match.index;

    if (start > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, start) });
    }

    segments.push({ type: 'image', alt: alt || 'Property photo', src });
    lastIndex = start + raw.length;
  }

  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) });
  }

  return segments;
};

export const AIChat = ({ isOpen, onClose, context, isFullScreen = false }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadPrompts();
  }, [context]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const loadPrompts = async () => {
    try {
      const pageType = context?.property_id ? 'detail' : context?.search ? 'search' : 'landing';
      const res = await getSuggestedPrompts({
        page_type: pageType,
        property_id: context?.property_id || ''
      });
      setPrompts(res.data.prompts || []);
    } catch (e) {
      setPrompts([
        'Find me homes under $500K in Austin',
        'Can I afford a $600K home?',
        'Tell me about schools in Seattle',
      ]);
    }
  };

  const handleSend = async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg) return;

    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await sendChatMessage({
        messages: newMessages,
        context: context || {},
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        tool_calls: res.data.tool_calls,
      }]);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const containerClass = 'flex flex-col h-full bg-background';

  if (!isOpen && !isFullScreen) return null;

  return (
    <div className={containerClass} data-testid="ai-chat-panel">
      {/* Header */}
      {!isFullScreen && (
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">PropertyAI Advisor</h3>
              <p className="text-xs text-muted-foreground">Powered by GPT-4o</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="ai-chat-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-7 h-7 text-primary" />
            </div>
            <h3 className="font-semibold text-lg mb-2">PropertyAI Advisor</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              Ask me anything about properties, valuations, neighborhoods, or mortgage affordability.
            </p>
            <div className="space-y-2">
              {prompts.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => handleSend(prompt)}
                  className="block w-full text-left px-4 py-2.5 rounded-xl border border-border bg-background hover:bg-secondary/50 text-sm transition-colors"
                  data-testid={`ai-prompt-${i}`}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
              msg.role === 'user'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background border border-border shadow-sm'
            }`}>
              {msg.role === 'assistant' ? (
                (() => {
                  const segments = splitContentWithImages(msg.content || '');
                  return (
                    <>
                      {segments.map((segment, segmentIndex) =>
                        segment.type === 'text' ? (
                          <ReactMarkdown
                            key={`text-${segmentIndex}`}
                            remarkPlugins={[remarkGfm]}
                            disallowedElements={['img']}
                            components={{
                              p: ({ node, ...props }) => <p className="mb-2 last:mb-0 leading-6" {...props} />,
                              ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-3 space-y-1" {...props} />,
                              ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-3 space-y-2" {...props} />,
                              li: ({ node, ...props }) => <li className="leading-6" {...props} />,
                              strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                              a: ({ node, ...props }) => <a className="text-primary underline" target="_blank" rel="noreferrer" {...props} />,
                              hr: ({ node, ...props }) => <hr className="my-3 border-border" {...props} />,
                              code: ({ node, inline, className, children, ...props }) =>
                                inline ? (
                                  <code className="px-1 py-0.5 rounded bg-secondary text-foreground" {...props}>{children}</code>
                                ) : (
                                  <pre className="my-2 p-3 rounded-lg bg-secondary overflow-x-auto">
                                    <code className={className} {...props}>{children}</code>
                                  </pre>
                                ),
                            }}
                          >
                            {segment.value}
                          </ReactMarkdown>
                        ) : (
                          <img
                            key={`img-${segment.src}-${segmentIndex}`}
                            src={segment.src}
                            alt={segment.alt}
                            className="my-2 rounded-lg border border-border w-full max-w-[220px] max-h-[150px] object-cover"
                            loading="lazy"
                          />
                        )
                      )}
                    </>
                  );
                })()
              ) : (
                <div className="whitespace-pre-wrap">{msg.content}</div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-background border border-border rounded-2xl px-4 py-3 shadow-sm">
              <div className="flex gap-1.5">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-border bg-background">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about properties, values, or neighborhoods..."
            className="flex-1 resize-none border border-border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background min-h-[40px] max-h-[120px]"
            rows={1}
            data-testid="ai-chat-input"
          />
          <Button
            onClick={() => handleSend()}
            disabled={!input.trim() || loading}
            className="rounded-xl h-10 w-10 p-0 bg-primary hover:bg-primary/90"
            data-testid="ai-chat-send-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIChat;

